// src/lib/contracts/templates.js
// Body content for each of the five HomeIQa contract templates.
// Source documents in /resources/. Legal language preserved verbatim;
// merge fields rendered via blank() so empty values appear as classic
// underlined placeholders and filled values appear as bold + underlined.

import { blank, fmtMoney, fmtMoneyAmount, fmtDate, fmtDateOrdinal, dateParts, escapeHtml } from './format';

export const CONTRACT_META = {
  psa: {
    id: 'psa',
    label: 'Purchase & Sale Agreement',
    docTitle: 'CONTRACT FOR THE SALE & PURCHASE OF REAL ESTATE',
    subtitle: 'Real Estate Purchase Agreement',
    signers: [
      { role: 'seller', label: 'Seller' },
      { role: 'buyer',  label: 'Buyer' }
    ]
  },
  assignment: {
    id: 'assignment',
    label: 'Assignment of Contract of Sale',
    docTitle: 'ASSIGNMENT OF CONTRACT OF SALE',
    subtitle: 'Contract Assignment',
    signers: [
      { role: 'assignor', label: 'Assignor' },
      { role: 'assignee', label: 'Assignee' }
    ]
  },
  novation: {
    id: 'novation',
    label: 'Authorization to Sign Listing Documents',
    docTitle: 'AUTHORIZATION TO SIGN LISTING DOCS AND OFFERS',
    subtitle: 'Limited Power of Attorney',
    signers: [
      { role: 'seller', label: 'Seller' }
    ]
  },
  cancellation: {
    id: 'cancellation',
    label: 'Cancellation of Purchase and Sale Contract',
    docTitle: 'CANCELLATION OF PURCHASE AND SALE CONTRACT',
    subtitle: 'Mutual Termination',
    signers: [
      { role: 'buyer',  label: 'Buyer' },
      { role: 'seller', label: 'Seller' }
    ]
  },
  jv: {
    id: 'jv',
    label: 'Real Estate Joint Venture Agreement',
    docTitle: 'REAL ESTATE JOINT VENTURE AGREEMENT',
    subtitle: 'Assignment Fee Split',
    signers: [
      { role: 'signor',  label: 'Signor' },
      { role: 'partner', label: 'JV Partner' }
    ]
  },
  amendment: {
    id: 'amendment',
    label: 'Amendment to Purchase & Sale Agreement',
    docTitle: 'AMENDMENT TO REAL ESTATE PURCHASE AND SALE AGREEMENT',
    subtitle: 'Contract Amendment',
    signers: [
      { role: 'seller',  label: 'Seller' },
      { role: 'seller2', label: 'Seller' },
      { role: 'buyer',   label: 'Buyer' },
      { role: 'buyer2',  label: 'Buyer' }
    ]
  }
};

// Each template returns just the BODY HTML (everything between the doc title
// and the signature blocks). The wrapper in render.js adds letterhead, title,
// signatures, and footer.

// ──────────────────────────────────────────────────────────────────────
// 1. PURCHASE & SALE AGREEMENT
// ──────────────────────────────────────────────────────────────────────
export function psaBody(f) {
  return `
    <h2>Parties</h2>
    <p>
      Their heir(s), successors, administrators and assigns, as Seller whose address is
      ${blank(f.sellerAddress, { width: '4in' })}, and ${blank(f.buyerName, { width: '3in' })}
      and/or assigns, their heirs, successors, administrators and assigns, as Buyer.
    </p>

    <p>
      <strong>WITNESSETH:</strong> That Seller, in consideration of the payments, covenants, agreements
      and conditions herein contained which on the part of the Buyer are to be made, done and performed,
      has this day sold, upon the conditions hereinafter recited, to the Buyer the real property legally
      described as:
    </p>

    <p>
      <strong>SUBJECT PROPERTY:</strong> ${blank(f.subjectProperty, { width: '5in' })}
      hereinafter the property,
    </p>

    <p>
      <strong>LEGAL DESCRIPTION:</strong> ${blank(f.legalDescription, { width: '5in' })}
    </p>

    <p>
      <strong>PURCHASE PRICE:</strong> ${blank(fmtMoney(f.purchasePrice), { width: '3in' })}
    </p>

    <h2>Terms &amp; Conditions</h2>

    ${f.assetType === 'land' ? `
    <p>
      <strong>PROPERTY DESCRIPTION:</strong> The Property is vacant land, sold together with all rights,
      privileges, easements, and appurtenances belonging thereto. No dwelling, structure, building,
      fixtures, or personal property are included in this sale except as expressly noted herein.
    </p>` : `
    <p>
      <strong>PROPERTY DESCRIPTION:</strong> Property including any fixtures, window and floor coverings,
      built-in appliances, draperies including hardware, shades, blinds, window and door screens, awnings,
      outdoor plants, trees, and other permanently attached items now on premises.
    </p>`}

    <p>
      <strong>EXISTING MORTGAGE(S):</strong> The existing financing, or mortgage, on the property will be
      paid from the Seller's proceeds of the sale.
    </p>

    <p>
      <strong>EXPENSES:</strong> Buyer to pay all closing costs. Buyer is also responsible for purchase of
      a survey if a survey is required.
    </p>

    <p>
      <strong>PRORATIONS:</strong> The purchase price shall be paid to the SELLER at closing. Any and all:
      property tax, rent prorations, association or condominium fees, mortgages, liens, judgments, taxes,
      special assessments and encumbrances, etc. will be paid by SELLER at closing to provide marketable
      title. Any and all tenant security deposits, and prepaid rental payments will be transferred to BUYER
      at closing.
    </p>

    <p>
      <strong>NO JUDGMENTS:</strong> Seller warrants that there are no judgments threatening the equity in
      subject property, and that there is no bankruptcy pending or contemplated by any titleholder. Seller
      will not further encumber the property, and an affidavit may be recorded at Buyer's expense putting
      the public on notice that the closing of this contract will extinguish liens and encumbrances
      hereafter recorded.
    </p>

    <p>
      <strong>LICENSURE:</strong> The trustee of the above-mentioned Buyer's Trust may, or may not, hold an
      inactive real estate license.
    </p>

    <p>
      <strong>POSSESSION:</strong> Possession of the property and occupancy (tenants excepted), with all
      keys and garage door openers, will be delivered to the Buyer when title transfers. Leases and
      security deposit will transfer to the Buyer with title.
    </p>

    <p>
      <strong>INSPECTIONS:</strong> This contract is contingent upon the Buyer's inspection and/or
      approval of the property prior to transfer of title. SELLER shall cooperate in making the property
      reasonably available for such inspection(s). Inspection may consist of an on-site walkthrough or a
      remote inspection.
    </p>

    <p>
      <strong>ACCEPTANCE:</strong> This instrument will become a binding contract when accepted by the
      Seller and signed by both Buyer and Seller. If it is not accepted and signed by the Seller prior to:
      ${blank(fmtDate(f.acceptanceDeadline), { width: '2in' })}, this contract shall be void.
    </p>

    <p>
      <strong>CLOSING:</strong> Buyer agrees that closing will take place on or before:
      ${blank(fmtDate(f.closingDate), { width: '2in' })}. BUYER will choose the closing agent. SELLER will
      convey title via Warranty Deed at closing and provide marketable and insurable title, free and clear
      of any lien or encumbrance. SELLER agrees to cooperate with the closing agent for execution of any
      and all corrective instruments as required to cure/clear title, both within 90 days and at SELLER's
      expense. This transaction will then be closed within 30 days after delivery to the BUYER of evidence
      that such defects have been cured. If the SELLER is unable to deliver marketable title, BUYER will
      have the right to terminate this agreement. This Agreement is assignable and BUYER's title vesting
      is to be determined before closing.
    </p>

    <p>
      <strong>OTHER AGREEMENTS:</strong> Buyer Agrees to Purchase Property in "As Is" Condition. Buyer to
      Pay All Closing Cost. This contract allows the BUYER to record a memorandum of agreement with the
      appropriate county department to show an agreement for sale is executed.
    </p>

    <p>
      <strong>TIME IS OF THE ESSENCE</strong> with this agreement. Each contingency contained herein
      shall be satisfied according to its terms by the closing date or this contract extends to provide
      time for satisfaction of said contingencies. Each party shall diligently pursue the completion of
      this transaction. Each warranty herein made survives the closing of this transaction.
    </p>

    <p>
      <strong>PROHIBITION:</strong> This agreement establishes a prohibition against transfer, conveyance
      or encumbrance to the property.
    </p>

    <h2>Miscellaneous</h2>

    <ol class="clauses">
      <li>For all purposes herein, an electronic signature shall be deemed the same as written signature.</li>
      <li>Buyer may use private money source for purchase funds.</li>
      <li>Buyer has the right to market the property in any way, not limited to investor networks or MLS, and can sign any disclosures necessary during the transaction.</li>
    </ol>
  `;
}

// ──────────────────────────────────────────────────────────────────────
// 2. ASSIGNMENT OF CONTRACT OF SALE
// ──────────────────────────────────────────────────────────────────────
export function assignmentBody(f) {
  const dp = dateParts(f.assignmentDate);
  return `
    <p>
      THIS ASSIGNMENT is made this ${blank(dp.day, { width: '0.5in' })} day of
      ${blank(dp.month, { width: '1.6in' })} 20${blank(dp.year, { width: '0.5in' })}.
    </p>

    <p>
      <strong>WHEREAS,</strong> ${blank(f.assignorName, { width: '3in' })} (hereinafter "Assignor"),
      with a business address of ${blank(f.assignorAddress, { width: '4in' })}, is the Buyer in a certain
      Contract of Sale (herein referred to as, the "Contract") for the purpose of transferring the real
      property located at ${blank(f.subjectProperty, { width: '4in' })} (hereinafter the "Property"); and
    </p>

    <p>
      <strong>WHEREAS,</strong> ${blank(f.assigneeName, { width: '3in' })} (hereinafter "Assignee"),
      with a business address of ${blank(f.assigneeAddress, { width: '4in' })}, is desirous of purchasing
      the Property; and
    </p>

    <p>
      <strong>WHEREAS,</strong> ${blank(f.sellerName, { width: '4in' })} is/are the Seller(s) (herein,
      the "Seller") of the aforementioned Property specified in the Contract.
    </p>

    <p>
      <strong>WHEREAS,</strong> Assignee has agreed to pay Assignor a Marketing Fee ("Assignment Fee"),
      all payable upon the successful closing, funding and full performance by Seller under the terms of
      the Contract. <strong>The Assignment Fee shall be calculated as the balance of the proceeds between
      the Assignee's Total Purchase Price and the Assignor Contract Price with the Seller(s).</strong>
    </p>

    <p>
      <strong>PURCHASE PRICE:</strong> The Assignor agrees to convey the property to Assignee for the sum
      of ${blank(fmtMoney(f.purchasePrice), { width: '2.5in' })}
    </p>

    <ol class="clauses">
      <li>The rights and liabilities set forth in the Contract and all addenda shall be and are hereby assigned from Assignor to Assignee for the Assignment Fee and for other good and valuable consideration paid by Assignee as delineated as follows:</li>
      <li>Closing to be set as soon as possible for all parties, but no later than ${blank(fmtDate(f.closingDate), { width: '2in' })}. All parties grant any additional time necessary to complete the closing paperwork.</li>
      <li>Assignee will be charged an administrative fee of $495 on the HUD. <strong>The balance of the funds shall be the assignment fee.</strong></li>
      <li>This Agreement shall be binding and enforceable only at such time as the Assignee shall make a good faith non-refundable deposit of ${blank(f.earnestMoneyAmount, { width: '2in' })} Dollars (${blank(fmtMoneyAmount(f.earnestMoneyAmount), { width: '1.2in' })}) payable to and received by the Assignor via readily available funds. Earnest money is non-refundable to the buyer unless title is non-conveyable. Earnest money will be applied towards the total purchase price of the property. This agreement may be void by the Assignor if Earnest Money is not deposited within the next business day.</li>
      <li>To the extent Seller fails to perform pursuant to the Contract or is otherwise in breach of Seller's obligations to Assignor under the Contract, the Assignor may declare this Agreement null and void solely in Assignor's sole and absolute discretion. To the extent Assignor declares this Agreement void, Assignee shall be entitled to a full refund of any amounts collected by the Assignor hereunder from the Assignee.</li>
      <li>Settlement shall occur as is indicated in the Contract time being of the essence.</li>
      <li>This assignment contract may not be assigned.</li>
      <li>This assignment contract is not contingent on buyers' inspection of the property.</li>
      <li>This assignment contract is not contingent on the buyer obtaining financing.</li>
      <li>This contract is contingent on clear title.</li>
      <li>In the event buyer is unable to close before the date stated in section (2), buyer(s) and Assignor(s) hereby authorize the Title or Closing Agency to release earnest money to the Assignor upon demand.</li>
      <li>All parties agree to use ${blank(f.titleCompany, { width: '3in' })} for Title and Settlement services, including the issuance of a title policy.</li>
      <li>All Recitals stated herein are incorporated herein as material terms of this Agreement.</li>
    </ol>

    <h2>Terms &amp; Conditions of Sale</h2>
    <p>
      Property is being sold "<strong>As Is</strong>." Buyer will pay their own closing costs. This
      transaction is not contingent on Buyer obtaining financing. Any exceptions must be mentioned below
      in the special provisions.
    </p>

    <h2>Special Provisions</h2>
    <p style="min-height:64px;">${escapeHtml(f.specialProvisions || '')}</p>
  `;
}

// ──────────────────────────────────────────────────────────────────────
// 3. AUTHORIZATION TO SIGN LISTING DOCS (this project's "novation" doc)
// ──────────────────────────────────────────────────────────────────────
export function novationBody(f) {
  return `
    <p>
      <strong>BE IT ACKNOWLEDGED</strong> that I/we, ${blank(f.sellerName, { width: '5in' })},
      the "Seller", do hereby grant a limited and specific authorization to sign
      ${blank(f.attorneyInFactName, { width: '3in' })}, as my "Attorney-in-Fact".
    </p>

    <p>
      Said Attorney-in-Fact shall have full power and authority to undertake and perform the following
      acts on my behalf, related to ${blank(f.subjectProperty, { width: '4in' })} (the "Property"):
    </p>

    <ol class="clauses">
      <li>Seller specifically authorizes and gives permission to the Attorney-in-Fact to list the property on any and all multiple listing service(s) (MLS) for the purpose of marketing &amp; selling the Property. This includes executing listing agreement(s), listing agreement addendum(s), disclosures, sales contracts and addendums.</li>
    </ol>

    <p>
      The authority herein shall include such incidental acts as reasonably required to carry authorities
      granted herein.
    </p>

    <p>
      This authorization is effective upon execution. This authorization may be revoked when the above
      stated one (1) time power or responsibility has been completed.
    </p>

    <p>
      This authorization form shall automatically be revoked upon my death or incapacitation, provided
      any person relying on this power of attorney shall be given full rights to accept and reply upon
      the authority of the Attorney-in-Fact until the receipt of actual notice of revocation.
    </p>
  `;
}

// ──────────────────────────────────────────────────────────────────────
// 4. CANCELLATION OF PURCHASE AND SALE CONTRACT
// ──────────────────────────────────────────────────────────────────────
export function cancellationBody(f) {
  return `
    <p>
      <strong>Date:</strong> ${blank(fmtDate(f.cancellationDate), { width: '2.5in' })}
    </p>

    <p>
      <strong>Subject Property:</strong> ${blank(f.subjectProperty, { width: '5in' })}
    </p>

    <p>
      ${blank(f.buyerName, { width: '3in' })} is terminating the Purchase and Sale contract with the
      (Seller) ${blank(f.sellerName, { width: '3in' })}, effective immediately.
    </p>

    <p>
      <strong>Reason for Termination:</strong> ${blank(f.reasonForTermination, { width: '5in' })}
    </p>

    <p>
      ${blank(f.buyerName, { width: '3in' })} (Buyer) will no longer hold any interest in the property
      after this document is signed and executed. Additionally, releases other parties and Escrow Agent
      to said Agreement from any and all claims, actions or demands whatsoever.
    </p>

    <p>
      <strong>Special Provisions:</strong> ${blank(f.specialProvisions, { width: '5in' })}
    </p>

    <p>
      This agreement is executed on this day ${blank(fmtDateOrdinal(f.executionDate), { width: '3in' })}.
    </p>
  `;
}

// ──────────────────────────────────────────────────────────────────────
// 5. REAL ESTATE JOINT VENTURE AGREEMENT
// ──────────────────────────────────────────────────────────────────────
export function jvBody(f) {
  return `
    <p>
      <strong>THIS JOINT VENTURE AGREEMENT</strong> is made this date
      ${blank(fmtDate(f.jvDate), { width: '2in' })}, between ${blank(f.signorName, { width: '2.5in' })}
      and ${blank(f.partnerName, { width: '3in' })}, to begin the same day and shall be terminated at any time.
    </p>

    <p>
      ${blank(f.signorName, { width: '2.5in' })} and ${blank(f.partnerName, { width: '2.5in' })} will do a
      ${blank(f.splitLabel || '50/50', { width: '0.8in' })} split of the Assignment Fee
      (${blank(f.signorPct, { width: '0.5in' })}% to ${blank(f.signorName, { width: '2in' })} and
      ${blank(f.partnerPct, { width: '0.5in' })}% to ${blank(f.partnerName, { width: '2.5in' })}) for the
      purchase and sale of the following property:
    </p>

    <p>
      <strong>Property Address:</strong> ${blank(f.propertyAddress, { width: '5in' })}
    </p>

    <p>
      The written consent of all parties shall be required with respect to the management, conduct, and
      operation of the joint venture business.
    </p>

    <p>
      Each party may have other business interest and may engage in any other business or trade,
      profession, or employment whatsoever, on his own account, or in joint venture with or as an
      employee of or as an officer, director, or shareholder of any other person, firm, or corporation,
      and he shall not be required to devote his entire time to the business of the joint venture.
    </p>

    <p>
      No party shall be obligated to devote more time and attention to the conduct of the business of the
      joint venture than shall be required for the supervision of the ownership, operation, and management
      of the venture.
    </p>

    <p>
      The Joint Venture books shall be maintained as part of the books and records and both parties shall
      have the right to inspect the books at any time.
    </p>

    <p><strong>Additional Conditions:</strong></p>
    <p style="min-height:80px;">${escapeHtml(f.additionalConditions || '')}</p>
  `;
}

// ──────────────────────────────────────────────────────────────────────
// 6. AMENDMENT TO PURCHASE AND SALE AGREEMENT
// ──────────────────────────────────────────────────────────────────────
export function amendmentBody(f) {
  const { day, year2 } = dateParts(f.originalAgreementDate);
  return `
    <p>
      The undersigned parties to a Purchase and Sale Agreement dated
      ${blank(day && f.originalAgreementDate ? fmtDate(f.originalAgreementDate) : '', { width: '2.4in' })},
      20${blank(year2 || '', { width: '0.5in' })},
      by and between ${blank(f.sellerName, { width: '3in' })} (&ldquo;Seller&rdquo;), and
      ${blank(f.buyerName, { width: '3in' })} (&ldquo;Buyer&rdquo;), for the purchase and sale of that
      certain property municipally known as:
    </p>

    <p style="margin-left:24px;">${blank(f.subjectProperty, { width: '4.5in' })}</p>

    <p>${blank(f.consentingParties || 'The Parties', { width: '1.6in' })} agree to amend said Agreement as follows:</p>

    <div style="min-height:2.6in;border:1px dashed rgba(107,30,46,0.25);border-radius:6px;padding:14px 16px;margin:8px 0 18px;">
      ${f.amendmentText
        ? `<div style="white-space:pre-wrap;">${escapeHtml(f.amendmentText)}</div>`
        : '<div style="color:#9A9A9A;font-style:italic;">(Amendment terms — describe each change to the original Agreement.)</div>'}
    </div>

    <p><strong>All other terms and conditions of the Sales Contract to remain the same.</strong></p>
  `;
}

export function bodyByType(type, fields) {
  switch (type) {
    case 'psa':           return psaBody(fields);
    case 'assignment':    return assignmentBody(fields);
    case 'novation':      return novationBody(fields);
    case 'cancellation':  return cancellationBody(fields);
    case 'jv':            return jvBody(fields);
    case 'amendment':     return amendmentBody(fields);
    default: throw new Error(`Unknown contract type: ${type}`);
  }
}
